import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type DataExportView,
  type ImportJobView,
  type ValidatedRow,
} from '../app/api.js'
import { ITEM_BULK_RESOURCE, type ImportJobOptions, type ItemImportResolution } from '@project/contracts'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDateTime } from '../framework/format.js'

/**
 * The three screens in the Data group (docs/11): bulk master loads, the GST
 * rate update, and the whole-account export.
 *
 * The import wizard is three steps and shows its working at every one, because
 * the thing that goes wrong with a bulk load is not that it fails — it is that
 * it succeeds and nobody notices it wrote the wrong thing to two thousand rows.
 */

/* ============================== imports =================================== */

const ACTION_WORDS: Readonly<Record<string, string>> = {
  CREATE: 'New record',
  UPDATE: 'Changed',
  UNCHANGED: 'No change',
  REJECT: 'Cannot be read',
  REVIEW: 'Needs review',
  SKIP: 'Duplicate · skipped',
}

/** Reads a picked file as base64, without the data: prefix. */
const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

/**
 * What the file as a whole needs answering before it can be imported: the
 * unit for item rows that have none, and whether the lookups it names that
 * are not there yet (item categories, mostly) are created first. Saved on
 * the job as soon as they are answered, so a refresh keeps them.
 */
const FileQuestions = ({ meta, blankUnits, busy, createMissing, onChange }: {
  meta: NonNullable<ImportJobView['meta']>
  blankUnits: number
  busy: boolean
  createMissing: ReadonlySet<string>
  onChange: (next: ImportJobOptions) => void
}) => {
  const [typedUnit, setTypedUnit] = useState(meta.options.defaultUnit ?? '')
  if (blankUnits === 0 && meta.missingLookups.length === 0) return null
  const choices = meta.unitChoices ?? []
  return (
    <div style={{ marginBottom: 16 }}>
      {blankUnits > 0 ? <Field
        label="Default unit"
        hint={`${blankUnits} row${blankUnits === 1 ? ' has' : 's have'} no unit. This unit is written into ${blankUnits === 1 ? 'it' : 'them'}.`}
      >
        {choices.length > 0 ? <Select
          aria-label="Default unit"
          value={meta.options.defaultUnit ?? ''}
          disabled={busy}
          placeholder="Choose a unit"
          options={choices.map((u) => ({ value: u, label: u }))}
          onChange={(unit) => onChange({ defaultUnit: unit || null })}
        /> : <div className="row">
          <Input aria-label="Default unit" value={typedUnit} disabled={busy} onChange={setTypedUnit} />
          <Button kind="ghost" disabled={busy || !typedUnit.trim()} onClick={() => onChange({ defaultUnit: typedUnit })}>
            Use this unit
          </Button>
        </div>}
      </Field> : null}
      {meta.missingLookups.map((m) => <div key={m.entity} style={{ marginTop: 12 }}>
        <label className="row">
          <input
            type="checkbox"
            checked={createMissing.has(m.entity)}
            disabled={busy || !m.creatable}
            onChange={(e) => {
              const next = new Set(createMissing)
              if (e.currentTarget.checked) next.add(m.entity)
              else next.delete(m.entity)
              onChange({ createMissing: [...next] })
            }}
          />
          Create missing {m.entityLabel.toLowerCase()} ({m.names.length})
        </label>
        <p className="helper">
          {m.fieldLabel} names {m.names.length === 1 ? 'one' : m.names.length} {m.entityLabel.toLowerCase()} not
          there yet, used on {m.rows} row{m.rows === 1 ? '' : 's'}: {m.names.slice(0, 12).join(', ')}
          {m.names.length > 12 ? ` and ${m.names.length - 12} more` : ''}.
          {createMissing.has(m.entity) ? ' They are created before the rows are imported.' : ' Those rows are refused unless they are created.'}
        </p>
      </div>)}
      {blankUnits > 0 && !meta.options.defaultUnit ? <Notice kind="warn">Choose the default unit before importing.</Notice> : null}
    </div>
  )
}

export const DataImports = ({ fixedResource }: { fixedResource?: string } = {}) => {
  const api = useApi()
  const client = useQueryClient()
  const filePicker = useRef<HTMLInputElement | null>(null)

  const resources = useQuery({ queryKey: ['import-resources'], queryFn: () => api.importResources() })
  const history = useQuery({ queryKey: ['import-history', api.companyId], queryFn: () => api.importHistory() })

  const [resource, setResource] = useState(fixedResource ?? '')
  const isItemBulk = resource === ITEM_BULK_RESOURCE
  // GST-Revised Tax Updates (SET4-001) downloads the items to revise.
  const isGst = resource === 'gst_tax_update'
  const [job, setJob] = useState<ImportJobView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [newCodes, setNewCodes] = useState<Record<number, string>>({})
  const [reviewOnly, setReviewOnly] = useState(false)
  const [rowPage, setRowPage] = useState(1)
  const showJob = (result: ImportJobView) => {
    setError(null); setJob(result); setNewCodes({}); setRowPage(1); setReviewOnly(false)
  }

  const validate = useMutation({
    mutationFn: async (file: File) => api.validateImport(resource, await readAsBase64(file), file.name),
    onSuccess: (result) => {
      showJob(result)
      void client.invalidateQueries({ queryKey: ['import-history', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const commit = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('Nothing to import.')
      return api.commitImport(job.id)
    },
    onSuccess: (result) => {
      setError(result.state === 'VALIDATED' ? 'Items changed while this upload was being reviewed. Check the newly flagged matches before importing.' : null)
      setJob(result); setRowPage(1)
      void client.invalidateQueries({ queryKey: ['import-history', api.companyId] })
      // A master that changed underneath every list on the screen.
      void client.invalidateQueries({ queryKey: ['masterList'] })
      void client.invalidateQueries({ queryKey: ['lookup'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const review = useMutation({
    mutationFn: (resolution: ItemImportResolution) => {
      if (!job) throw new Error('Nothing to review.')
      return api.reviewItemImport(job.id, resolution)
    },
    onSuccess: (result, resolution) => {
      setError(null); setJob(result)
      setNewCodes((prev) => { const next = { ...prev }; delete next[resolution.sheetRow]; return next })
      void client.invalidateQueries({ queryKey: ['import-history', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })
  const options = useMutation({
    mutationFn: (next: ImportJobOptions) => {
      if (!job) throw new Error('Nothing to change.')
      return api.setImportOptions(job.id, next)
    },
    onSuccess: (result) => {
      setError(null); setJob(result)
      void client.invalidateQueries({ queryKey: ['import-history', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })
  const resume = useMutation({
    mutationFn: (id: string) => api.importJob(id),
    onSuccess: (result) => { setResource(result.resource); showJob(result) },
    onError: (err) => setError(messageFor(err)),
  })
  const busy = validate.isPending || commit.isPending || review.isPending || resume.isPending || options.isPending

  if (resources.isLoading) return <Loading />
  if (resources.error) return <ErrorBlock error={resources.error} />

  const rowColumns: ReadonlyArray<ColumnDef<ValidatedRow>> = [
    { key: 'sheetRow', header: 'Row', width: 80, align: 'right' },
    { key: 'label', header: 'Record', render: (r) => <>
      <div>{r.label}</div>
      {isItemBulk ? <div className="helper">Unit: {String(r.values['unit'] ?? '—')}</div> : null}
      {isItemBulk && r.problem ? <div>{r.problem}</div> : null}
      {(r.warnings ?? []).map((w) => <div key={w} className="helper">{w}</div>)}
    </> },
    { key: 'action', header: 'What happens', width: 150, render: (r) => ACTION_WORDS[r.action] ?? r.action },
    ...(!isItemBulk ? [{
      key: 'changes',
      header: 'Changing',
      render: (r: ValidatedRow) => (r.problem
        ? r.problem
        : r.changes.length === 0
          ? '—'
          : r.changes.map((c) => `${c.field}: ${c.from} → ${c.to}`).join('; ')),
    }] : []),
    ...(isItemBulk ? [
      {
        key: 'duplicateMatches', header: 'Possible duplicates', width: 300,
        render: (r: ValidatedRow) => (r.duplicateMatches?.length ? <div>
          {r.duplicateMatches.slice(0, 5).map((match, index) => <div key={index} style={{ marginBottom: 10 }}>
            <strong>{match.code} — {match.name}</strong>
            <div className="helper">
              {match.source === 'EXISTING' ? `Existing item${match.retired ? ' · retired' : ''}` : `Upload row ${match.sheetRow}`}
              {' · '}{match.unit}<br />{match.reason}
            </div>
          </div>)}
          {r.duplicateMatches.length > 5 ? <details>
            <summary>{r.duplicateMatches.length - 5} more matches</summary>
            {r.duplicateMatches.slice(5).map((match, index) => <div key={index} style={{ marginTop: 10 }}>
              <strong>{match.code} — {match.name}</strong>
              <div className="helper">
                {match.source === 'EXISTING' ? `Existing item${match.retired ? ' · retired' : ''}` : `Upload row ${match.sheetRow}`}
                {' · '}{match.unit}<br />{match.reason}
              </div>
            </div>)}
          </details> : null}
        </div> : '—'),
      },
      {
        key: 'review', header: 'Your decision', width: 240,
        render: (r: ValidatedRow) => r.action === 'REJECT' ? 'Correct the spreadsheet and upload again.'
          : job?.state === 'IMPORTED' ? <div>
            {r.action === 'SKIP' ? 'Confirmed duplicate' : r.action === 'CREATE' ? 'Created new item' : ACTION_WORDS[r.action]}
            {r.review ? <div className="helper">Reviewed by {r.review.reviewedByName}</div> : null}
          </div> : <div>
            <Select aria-label={`Decision for row ${r.sheetRow}`}
              value={r.action === 'SKIP' ? 'DUPLICATE' : r.action === 'REVIEW' ? 'REVIEW' : 'NEW'}
              disabled={busy}
              options={[
                { value: 'REVIEW', label: 'Needs review' },
                { value: 'NEW', label: 'Create as new' },
                { value: 'DUPLICATE', label: 'Confirm duplicate · skip' },
              ]}
              onChange={(decision) => review.mutate({
                sheetRow: r.sheetRow, decision: decision as ItemImportResolution['decision'],
                ...(decision === 'NEW' ? { code: newCodes[r.sheetRow] ?? String(r.values['code']) } : {}),
              })}
            />
            {r.action !== 'SKIP' ? <div style={{ marginTop: 10 }}>
              <Input aria-label={`New item code for row ${r.sheetRow}`}
                value={newCodes[r.sheetRow] ?? String(r.values['code'] ?? '')} disabled={busy}
                onChange={(code) => setNewCodes((prev) => ({ ...prev, [r.sheetRow]: code }))} />
              <p className="helper">Use an unused code to create a separate item.</p>
              {newCodes[r.sheetRow] !== undefined ? <Button kind="ghost" disabled={busy || !newCodes[r.sheetRow]?.trim()}
                onClick={() => review.mutate({ sheetRow: r.sheetRow, decision: 'NEW', code: newCodes[r.sheetRow] })}>
                Save as new
              </Button> : null}
            </div> : null}
            {r.review ? <div className="helper">Reviewed by {r.review.reviewedByName}</div> : null}
          </div>,
      },
    ] : []),
  ]

  const groups = new Map<string, typeof resources.data>()
  for (const r of resources.data ?? []) {
    const bucket = groups.get(r.group)
    if (bucket) (bucket as Array<typeof r>).push(r)
    else groups.set(r.group, [r] as typeof resources.data)
  }

  const chosen = resources.data?.find((r) => r.key === resource)
  const changing = (job?.rows ?? []).filter((r) => r.action === 'CREATE' || r.action === 'UPDATE')
  const rejected = (job?.rows ?? []).filter((r) => r.action === 'REJECT')
  const pending = (job?.rows ?? []).filter((r) => r.action === 'REVIEW')
  const skipped = (job?.rows ?? []).filter((r) => r.action === 'SKIP')
  const hasUnsavedCodes = Object.keys(newCodes).length > 0
  const meta = job?.meta
  const blankUnits = (job?.rows ?? []).filter((r) => r.blankUnit && (r.action === 'CREATE' || r.action === 'REVIEW'))
  const needsUnit = blankUnits.length > 0 && !meta?.options.defaultUnit
  const createMissing = new Set(meta?.options.createMissing ?? [])
  const visibleRows = isItemBulk
    ? (job?.rows ?? []).filter((r) => !reviewOnly || r.action === 'REVIEW')
    : [...changing, ...rejected].sort((a, b) => a.sheetRow - b.sheetRow)
  const pageSize = 50
  const maxPage = Math.max(1, Math.ceil(visibleRows.length / pageSize))
  const currentPage = Math.min(rowPage, maxPage)

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      {!fixedResource ? <Notice kind="info">
        Load masters in this order, so every name a sheet uses is already there:
        Units → Item categories → Brands → Items. Exports from the reference system
        load as they are.
      </Notice> : null}

      {!fixedResource ? <Panel title="1 · What are you loading?">
        <Field label="Records">
          <Select
            value={resource}
            disabled={busy}
            onChange={(next) => { setResource(next); setJob(null); setError(null); setNewCodes({}); setRowPage(1) }}
            placeholder="Choose what to load"
            options={[...groups].flatMap(([group, list]) =>
              (list ?? []).map((r) => ({ value: r.key, label: `${group} — ${r.label}` })))}
          />
        </Field>
        {chosen ? <p className="helper">{chosen.blurb}</p> : null}
      </Panel> : null}

      {resource ? (
        <Panel title={isItemBulk ? 'Upload items' : isGst ? 'GST-Revised Tax Updates' : '2 · Download, edit, and put it back'}>
          <p className="helper">
            {isGst ? <>Select the file, validate it and click &lsquo;Import&rsquo; button. Download Items gives every
            item with its HSN/SAC code and tax rate; change the ones the revision touches and upload it.</> : isItemBulk ? <>
              Download the Excel template and fill in Item code, Name and Unit. Optional columns
              include category, tax and purchase cost. Upload an .xlsx file with up to 20,000 rows.
              Possible duplicates are flagged for your review. Confirm duplicates to skip them,
              or create separate items with unused codes.
            </> : <>The spreadsheet comes down filled in with what is already there. Change what you
            need, leave the rest, and upload it — an export from the old system works as it is.
            A blank code is matched on the name, or made for a new record; Active No retires a
            record. Nothing is written until you have seen what it would do.</>}
          </p>
          <div className="row row--wrap">
            <Button
              kind="ghost"
              disabled={busy}
              onClick={() => { window.location.href = api.importTemplateFor(resource) }}
            >
              {isItemBulk ? 'Download template' : isGst ? 'Download Items' : 'Download the spreadsheet'}
            </Button>
            <input
              ref={filePicker}
              type="file"
              accept=".xlsx"
              aria-label="Upload spreadsheet"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0]
                event.currentTarget.value = ''
                if (file) {
                  if (file.size > 25_000_000) { setError('The file is too large. Choose an .xlsx file smaller than 25 MB.'); return }
                  validate.mutate(file)
                }
              }}
            />
            <Button
              kind="primary"
              disabled={busy}
              onClick={() => filePicker.current?.click()}
            >
              {validate.isPending ? 'Reading…' : isItemBulk ? 'Choose file and validate' : 'Choose the edited file'}
            </Button>
          </div>
        </Panel>
      ) : null}

      {job ? (
        <Panel
          title={`3 · ${job.state === 'IMPORTED' ? 'Imported' : 'Check before importing'}`}
          aside={
            job.state === 'IMPORTED' ? null : (
              <Button
                kind="primary"
                disabled={busy || pending.length > 0 || hasUnsavedCodes || needsUnit || (changing.length === 0 && skipped.length === 0)}
                onClick={() => commit.mutate()}
              >
                {commit.isPending ? 'Importing…' : changing.length === 0 ? 'Finish review' : `Import ${changing.length} row${changing.length === 1 ? '' : 's'}`}
              </Button>
            )
          }
        >
          {job.state === 'IMPORTED' ? (
            <Notice kind="ok">
              Done. {job.createdCount} created, {job.updatedCount} changed.
              {isItemBulk ? ` ${skipped.length} confirmed duplicate${skipped.length === 1 ? '' : 's'} skipped.` : ''}
            </Notice>
          ) : (
            <p className="helper">
              {job.fileName ? `${job.fileName} · ` : ''}
              {changing.length} row{changing.length === 1 ? '' : 's'} ready to import ·
              {isItemBulk ? <>{' '}{pending.length} need review · {skipped.length} confirmed duplicate{skipped.length === 1 ? '' : 's'} · </>
                : <>{' '}{(job.rows.length - changing.length - rejected.length)} unchanged · </>}
              {' '}{rejected.length} could not be read.
              {rejected.length > 0 ? ' Rows that cannot be read are skipped, not guessed at.' : ''}
            </p>
          )}
          {meta && job.state !== 'IMPORTED' ? <FileQuestions
            meta={meta} blankUnits={blankUnits.length} busy={busy}
            createMissing={createMissing}
            onChange={(next) => options.mutate(next)}
          /> : null}
          {meta && meta.ignoredHeaders.length > 0 ? <div style={{ marginBottom: 16 }}>
            <strong>Columns not used</strong>
            <ul className="helper" aria-label="Columns not used">
              {meta.ignoredHeaders.map((h) => <li key={h.header}>{h.header} — {h.reason}</li>)}
            </ul>
          </div> : null}
          {pending.length > 0 ? <Notice kind="warn">Review all {pending.length} flagged row{pending.length === 1 ? '' : 's'} before importing.</Notice> : null}
          {hasUnsavedCodes ? <Notice kind="warn">Save the edited item codes before importing.</Notice> : null}
          {isItemBulk ? <label className="row" style={{ marginBottom: 16 }}>
            <input type="checkbox" checked={reviewOnly} onChange={(e) => { setReviewOnly(e.currentTarget.checked); setRowPage(1) }} />
            Show rows needing review only
          </label> : null}
          <Table
            columns={rowColumns}
            rows={visibleRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
            getKey={(r) => String(r.sheetRow)}
            empty={<Empty title={reviewOnly ? 'No rows need review' : 'No rows to import'} body={[]} />}
          />
          {maxPage > 1 ? <div className="pager">
            <Button kind="ghost" disabled={currentPage <= 1} onClick={() => setRowPage(currentPage - 1)}>Previous</Button>
            <span>Page {currentPage} of {maxPage}</span>
            <Button kind="ghost" disabled={currentPage >= maxPage} onClick={() => setRowPage(currentPage + 1)}>Next</Button>
          </div> : null}
        </Panel>
      ) : null}

      <Panel title="Earlier loads">
        {history.error ? <ErrorBlock error={history.error} /> : null}
        <Table
          columns={[
            { key: 'createdAt', header: 'When', width: 170, render: (r) => formatDateTime(r.createdAt) },
            { key: 'resourceLabel', header: 'What', width: 220 },
            { key: 'fileName', header: 'File', render: (r) => r.fileName ?? '—' },
            { key: 'state', header: 'State', width: 130 },
            { key: 'createdCount', header: 'Created', align: 'right', width: 100 },
            { key: 'updatedCount', header: 'Changed', align: 'right', width: 100 },
            { key: 'rejected', header: 'Invalid rows', align: 'right', width: 100 },
            { key: 'createdByName', header: 'By', width: 180, render: (r) => r.createdByName ?? '—' },
            { key: 'review', header: 'Review', width: 130, render: (r) => <Button kind="ghost" disabled={busy}
              onClick={() => resume.mutate(r.id)}>{r.state === 'VALIDATED' ? 'Resume review' : 'View details'}</Button> },
          ]}
          rows={(history.data ?? []).filter((r) => !fixedResource || r.resource === fixedResource)}
          getKey={(r) => r.id}
          empty={<Empty title="Nothing loaded yet" body={['Bulk loads appear here once one has been run.']} />}
        />
      </Panel>
    </>
  )
}

/* ============================== exports =================================== */

const EXPORT_WORDS: Readonly<Record<string, string>> = {
  REQUESTED: 'Being built',
  READY: 'Ready to download',
  EXPIRED: 'Expired',
  FAILED: 'Could not be built',
}

export const DataExports = () => {
  const api = useApi()
  const client = useQueryClient()
  const exports_ = useQuery({
    queryKey: ['data-exports', api.companyId],
    queryFn: () => api.dataExports(),
    // One is being built in the background; the row is what says when it is done.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((e: DataExportView) => e.state === 'REQUESTED') ? 5_000 : false,
  })
  const nextAllowed = useQuery({
    queryKey: ['data-export-next', api.companyId],
    queryFn: () => api.dataExportNextAllowed(),
  })
  const [error, setError] = useState<string | null>(null)

  const request = useMutation({
    mutationFn: () => api.requestDataExport(),
    onSuccess: () => {
      setError(null)
      void client.invalidateQueries({ queryKey: ['data-exports', api.companyId] })
      void client.invalidateQueries({ queryKey: ['data-export-next', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (exports_.isLoading) return <Loading />
  if (exports_.error) return <ErrorBlock error={exports_.error} />

  const columns: ReadonlyArray<ColumnDef<DataExportView>> = [
    { key: 'requestedAt', header: 'Asked for', width: 175, render: (r) => formatDateTime(r.requestedAt) },
    { key: 'requestedByName', header: 'By', width: 180, render: (r) => r.requestedByName ?? '—' },
    { key: 'state', header: 'State', width: 165, render: (r) => EXPORT_WORDS[r.state] ?? r.state },
    {
      key: 'byteSize',
      header: 'Size',
      align: 'right',
      width: 110,
      render: (r) => (r.byteSize === null ? '—' : `${Math.max(1, Math.round(r.byteSize / 1024))} KB`),
    },
    {
      key: 'expiresAt',
      header: 'Available until',
      width: 175,
      render: (r) => (r.expiresAt ? formatDateTime(r.expiresAt) : '—'),
    },
    {
      key: 'download',
      header: '',
      width: 150,
      render: (r) => (r.downloadable ? (
        <Button kind="ghost" onClick={() => { window.location.href = api.dataExportUrl(r.id) }}>
          Download
        </Button>
      ) : <span className="helper">{r.note ?? ''}</span>),
    },
  ]

  const blocked = nextAllowed.data?.at ?? null

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}
      <Notice kind="info">
        An export holds every document and every master in this company, as spreadsheets,
        in one archive. The archive is password protected and the password is emailed to
        the management contact named in Settings (or, if none is named, to whoever asked) — it
        is not shown here and cannot be read back. The download
        stops working four days after it is built, and one export can be asked for every
        seven days.
      </Notice>

      <Panel
        title="Export everything"
        aside={
          <Button
            kind="primary"
            disabled={request.isPending || blocked !== null}
            onClick={() => request.mutate()}
          >
            Ask for an export
          </Button>
        }
      >
        {blocked ? (
          <p className="helper">
            The next export can be asked for on {formatDateTime(blocked)}.
          </p>
        ) : null}
        <Table
          columns={columns}
          rows={exports_.data ?? []}
          getKey={(r) => r.id}
          empty={<Empty title="No exports yet" body={['Nothing has been exported from this company.']} />}
        />
      </Panel>
    </>
  )
}

/* ============================== branding ================================== */

export const Branding = () => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement | null>(null)
  const branding = useQuery({ queryKey: ['branding', api.companyId], queryFn: () => api.branding() })

  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ website: string; tanNo: string; cinNo: string } | null>(null)

  const current = branding.data
  const values = draft ?? {
    website: current?.website ?? '',
    tanNo: current?.tanNo ?? '',
    cinNo: current?.cinNo ?? '',
  }

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.saveBranding>[0]) => api.saveBranding(input),
    onSuccess: () => {
      setError(null)
      setDraft(null)
      void client.invalidateQueries({ queryKey: ['branding', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (branding.isLoading) return <Loading />
  if (branding.error) return <ErrorBlock error={branding.error} />

  const pickLogo = async (file: File): Promise<void> => {
    // Both checks are made again on the server against the bytes; this one is
    // only so the answer is instant.
    if (file.size > 100 * 1024) {
      setError(`That logo is ${Math.round(file.size / 1024)} kB. It has to be under 100 kB.`)
      return
    }
    const base64 = await readAsBase64(file)
    save.mutate({ logo: `data:image/png;base64,${base64}` })
  }

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <Panel title="The sign-in page">
        <p className="helper">
          The logo appears on the sign-in page and at the top of every printed document.
          It has to be a PNG, under 100 kB.
        </p>
        <div className="row row--wrap" style={{ alignItems: 'flex-start', gap: 24 }}>
          <div
            style={{
              border: '1px solid var(--line)',
              padding: 16,
              minWidth: 220,
              minHeight: 96,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--surface-alt)',
            }}
          >
            {current?.logo
              ? <img src={current.logo} alt="The company logo" style={{ maxHeight: 64, maxWidth: 200 }} />
              : <span className="helper">No logo set</span>}
          </div>
          <div className="row row--wrap">
            <input
              ref={picker}
              type="file"
              accept="image/png"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0]
                event.currentTarget.value = ''
                if (file) void pickLogo(file)
              }}
            />
            <Button kind="primary" disabled={save.isPending} onClick={() => picker.current?.click()}>
              {current?.logo ? 'Replace the logo' : 'Upload a logo'}
            </Button>
            {current?.logo ? (
              <Button kind="ghost" disabled={save.isPending} onClick={() => save.mutate({ logo: null })}>
                Remove it
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      <Panel
        title="What is printed under the company name"
        aside={
          <Button
            kind="primary"
            disabled={save.isPending || draft === null}
            onClick={() => save.mutate(values)}
          >
            Save
          </Button>
        }
      >
        <div className="form-grid">
          <Field label="Website">
            <Input value={values.website} onChange={(website) => setDraft({ ...values, website })} />
          </Field>
          <Field label="TAN" hint="Printed on documents that need it.">
            <Input value={values.tanNo} onChange={(tanNo) => setDraft({ ...values, tanNo })} />
          </Field>
          <Field label="CIN">
            <Input value={values.cinNo} onChange={(cinNo) => setDraft({ ...values, cinNo })} />
          </Field>
        </div>
      </Panel>
    </>
  )
}
